import { useState, useEffect, type ReactNode } from "react";
import { Phone, MessageCircle, Menu, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
const logoImg = "/logo.webp";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";

const pageNames: Record<string, string> = {
  "/": "Home",
  "/psicoterapia-adulto": "Psicoterapia Adulto",
  "/psicoterapia-infantil": "Psicoterapia Infantil",
  "/psicoterapia-adolescente": "Psicoterapia Adolescente",
  "/psicoterapia-melhor-idade": "Psicoterapia Melhor Idade",
  "/psicoterapia-casal": "Psicoterapia de Casal",
  "/psicoterapia-breve": "Psicoterapia Breve",
  "/tcc": "Psicoterapia TCC",
  "/orientacao-vocacional": "Orientação Vocacional",
  "/reorientacao-carreira": "Reorientação de Carreira",
  "/avaliacao-psicodiagnostico": "Psicodiagnóstico",
  "/avaliacao-neuropsicologica": "Avaliação Neuropsicológica",
  "/avaliacao-laudo-cirurgia": "Laudo para Cirurgia",
  "/pericia-psicologica": "Perícia Psicológica",
  "/contato": "Contato",
  "/equipe": "Equipe",
};

const psicoterapias = [
  { name: "Psicoterapia Infantil", href: "/psicoterapia-infantil" },
  { name: "Psicoterapia Adolescente", href: "/psicoterapia-adolescente" },
  { name: "Psicoterapia Adulto", href: "/psicoterapia-adulto" },
  { name: "Psicoterapia de Casal", href: "/psicoterapia-casal" },
  { name: "Terapia Melhor Idade", href: "/psicoterapia-melhor-idade" },
  { name: "Psicoterapia Breve", href: "/psicoterapia-breve" },
  { name: "TCC", href: "/tcc" },
];

const outros = [
  { name: "Teste Avaliação Neuropsicológica", href: "/avaliacao-neuropsicologica" },
  { name: "Avaliação / Laudo para Cirurgia", href: "/avaliacao-laudo-cirurgia" },
  { name: "Avaliação Psicodiagnóstico", href: "/avaliacao-psicodiagnostico" },
  { name: "Perícia Psicológica", href: "/pericia-psicologica" },
  { name: "Orientação Vocacional", href: "/orientacao-vocacional" },
  { name: "Reorientação de Carreira e de Vida", href: "/reorientacao-carreira" },
];

const isSpaRoute = (href: string) => href.startsWith("/") && !href.includes("#");

const MenuLink = ({
  href,
  className,
  children,
  onClick,
}: {
  href: string;
  className: string;
  children: ReactNode;
  onClick?: () => void;
}) => {
  if (isSpaRoute(href)) {
    return (
      <Link to={href} onClick={onClick} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} onClick={onClick} className={className}>
      {children}
    </a>
  );
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const location = useLocation();
  const pageName = pageNames[location.pathname] || "Site";
  const whatsappMessage = encodeURIComponent(`Oi! Vim pelo site da Equilybra (${pageName}) e gostaria de saber como funciona.`);
  const whatsappUrl = `https://wa.me/5511976974905?text=${whatsappMessage}`;
  const [activeHash, setActiveHash] = useState<string>("");

  // Observa a seção #sobre na home para alternar destaque entre Home e Sobre
  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveHash("");
      return;
    }
    const section = document.getElementById("sobre");
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setActiveHash(entry.isIntersecting ? "sobre" : "");
      },
      { rootMargin: "-40% 0px -50% 0px", threshold: 0 },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [location.pathname]);

  const toggleSubmenu = (menu: string) => {
    setOpenSubmenu(openSubmenu === menu ? null : menu);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md shadow-soft">
      <div className="bg-primary">
        <div className="container mx-auto px-4 flex items-center justify-between py-2 text-sm">
          <span className="text-primary-foreground font-medium hidden sm:block header-glow-text">
            Equilybra Psicologia — Cuidando de você
          </span>
          <div className="flex items-center gap-4 text-primary-foreground mx-auto sm:mx-0">
            <a
              href="tel:1123050747"
              className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
            >
              <Phone className="w-3.5 h-3.5" />
              <span>(11) 2305-0747</span>
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 hover:opacity-80 transition-opacity"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">(11) 97697-4905 (somente mensagem)</span>
              <span className="sm:hidden">WhatsApp</span>
            </a>
            <span className="ml-1 inline-flex">
              <ThemeToggle variant="topbar" className="w-7 h-7" />
            </span>
          </div>
        </div>
      </div>

      <nav aria-label="Navegação principal" className="container mx-auto px-4 py-0 flex items-center justify-between">
        <MenuLink href="/" className="flex items-center">
          <img src={logoImg} alt="Equilybra Clínica de Psicologia" width="240" height="230" fetchPriority="high" decoding="async" className="h-20 w-auto object-contain header-glow-logo" />
        </MenuLink>

        <ul className="hidden lg:flex items-center gap-1">
          <NavItem href="/" label="Home" currentPath={location.pathname} activeHash={activeHash} />
          <NavItem href="/#sobre" label="Sobre" currentPath={location.pathname} activeHash={activeHash} />
          <NavItem href="/equipe" label="Equipe" currentPath={location.pathname} />
          <DropdownNavItem label="Psicoterapias" items={psicoterapias} currentPath={location.pathname} />
          <DropdownNavItem label="Outros" items={outros} currentPath={location.pathname} />
          <NavItem href="/blog" label="Blog" currentPath={location.pathname} />
          <NavItem href="/ebooks" label="Ebooks" currentPath={location.pathname} />
          <NavItem href="/contato" label="Contato" currentPath={location.pathname} />

        </ul>

        <div className="hidden lg:flex items-center gap-2">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            title="WhatsApp"
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-whatsapp text-primary-foreground hover:bg-whatsapp-hover transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
          </a>
          <a
            href="tel:1123050747"
            aria-label="Ligar agora"
            title="Ligue Agora"
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          >
            <Phone className="w-5 h-5" />
          </a>
        </div>


        <div className="flex items-center gap-1 lg:hidden">
          <ThemeToggle />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Abrir menu de navegação"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm p-0 flex flex-col">
              <SheetHeader className="p-4 border-b border-border flex-row items-center justify-between space-y-0">
                <SheetTitle className="text-left font-heading">Menu</SheetTitle>
                <ThemeToggle />
              </SheetHeader>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
              <MobileNavItem href="/" label="Home" onClick={() => setMobileOpen(false)} currentPath={location.pathname} activeHash={activeHash} />
              <MobileNavItem href="/#sobre" label="Sobre" onClick={() => setMobileOpen(false)} currentPath={location.pathname} activeHash={activeHash} />
              <MobileNavItem href="/equipe" label="Equipe" onClick={() => setMobileOpen(false)} currentPath={location.pathname} />

              <div>
                <button
                  type="button"
                  onClick={() => toggleSubmenu("psicoterapias")}
                  aria-expanded={openSubmenu === "psicoterapias"}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-foreground hover:bg-secondary transition-colors font-medium"
                >
                  Psicoterapias
                  <ChevronDown className={`w-4 h-4 transition-transform ${openSubmenu === "psicoterapias" ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {openSubmenu === "psicoterapias" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 space-y-1">
                        {psicoterapias.map((item) => {
                          const active = item.href === location.pathname;
                          return (
                            <MenuLink
                              key={item.name}
                              href={item.href}
                              onClick={() => setMobileOpen(false)}
                              className={`block px-4 py-2 text-sm rounded-lg transition-colors ${
                                active
                                  ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary"
                                  : "text-muted-foreground hover:text-primary hover:bg-secondary"
                              }`}
                            >
                              {item.name}
                            </MenuLink>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => toggleSubmenu("outros")}
                  aria-expanded={openSubmenu === "outros"}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-foreground hover:bg-secondary transition-colors font-medium"
                >
                  Outros
                  <ChevronDown className={`w-4 h-4 transition-transform ${openSubmenu === "outros" ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {openSubmenu === "outros" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 space-y-1">
                        {outros.map((item) => {
                          const active = item.href === location.pathname;
                          return (
                            <MenuLink
                              key={item.name}
                              href={item.href}
                              onClick={() => setMobileOpen(false)}
                              className={`block px-4 py-2 text-sm rounded-lg transition-colors ${
                                active
                                  ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary"
                                  : "text-muted-foreground hover:text-primary hover:bg-secondary"
                              }`}
                            >
                              {item.name}
                            </MenuLink>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <MobileNavItem href="/blog" label="Blog" onClick={() => setMobileOpen(false)} currentPath={location.pathname} />
              <MobileNavItem href="/ebooks" label="Ebooks" onClick={() => setMobileOpen(false)} currentPath={location.pathname} />
              <MobileNavItem href="/contato" label="Contato" onClick={() => setMobileOpen(false)} currentPath={location.pathname} />


              <div className="flex gap-2 pt-4">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-whatsapp text-primary-foreground font-semibold text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
                <a
                  href="tel:1123050747"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-primary text-primary-foreground font-semibold text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Ligue Agora
                </a>
              </div>
            </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};


const isItemActive = (href: string, currentPath: string, activeHash = "") => {
  if (href.startsWith("/#")) {
    const hash = href.slice(2);
    return currentPath === "/" && activeHash === hash;
  }
  if (href === "/") return currentPath === "/" && activeHash === "";
  if (href === "/blog") return currentPath === "/blog" || currentPath.startsWith("/blog/");
  if (href === "/ebooks") return currentPath === "/ebooks" || currentPath.startsWith("/ebooks/");
  return currentPath === href;
};


const NavItem = ({ href, label, currentPath, activeHash = "" }: { href: string; label: string; currentPath: string; activeHash?: string }) => {
  const active = isItemActive(href, currentPath, activeHash);
  return (
    <li>
      <MenuLink
        href={href}
        className={`relative px-4 py-2 rounded-lg transition-colors font-medium text-sm ${
          active
            ? "text-primary bg-primary/10 after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:bg-primary after:rounded-full"
            : "text-foreground hover:bg-secondary hover:text-primary"
        }`}
      >
        {label}
      </MenuLink>
    </li>
  );
};

const DropdownNavItem = ({
  label,
  items,
  currentPath,
}: {
  label: string;
  items: { name: string; href: string }[];
  currentPath: string;
}) => {
  const groupActive = items.some((i) => i.href === currentPath);
  return (
    <li className="relative group">
      <button
        className={`flex items-center gap-1 px-4 py-2 rounded-lg transition-colors font-medium text-sm ${
          groupActive
            ? "text-primary bg-primary/10 after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:bg-primary after:rounded-full"
            : "text-foreground hover:bg-secondary hover:text-primary"
        }`}
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
      </button>
      <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
        <div className="bg-card rounded-xl shadow-lg border border-border p-2 min-w-[260px]">
          {items.map((item) => {
            const active = item.href === currentPath;
            return (
              <MenuLink
                key={item.name}
                href={item.href}
                className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${
                  active
                    ? "bg-primary/10 text-primary font-semibold border-l-2 border-primary"
                    : "text-foreground hover:bg-secondary hover:text-primary"
                }`}
              >
                {item.name}
              </MenuLink>
            );
          })}
        </div>
      </div>
    </li>
  );
};

const MobileNavItem = ({
  href,
  label,
  onClick,
  currentPath,
  activeHash = "",
}: {
  href: string;
  label: string;
  onClick: () => void;
  currentPath: string;
  activeHash?: string;
}) => {
  const active = isItemActive(href, currentPath, activeHash);
  return (
    <MenuLink
      href={href}
      onClick={onClick}
      className={`block px-4 py-3 rounded-lg transition-colors font-medium ${
        active
          ? "bg-primary/10 text-primary border-l-4 border-primary"
          : "text-foreground hover:bg-secondary"
      }`}
    >
      {label}
    </MenuLink>
  );
};

export default Navbar;
